import React, { useState } from 'react';
import {
  Form, Button, Alert, Spinner,
} from 'react-bootstrap';
import BookingService from '../../../Services/BookingService';

const ORIGIN_OPTIONS = [
  {
    value: 'whatsapp_respondio', label: 'Whatsapp (respond.io)', specLabel: 'Conversation ID', specPlaceholder: 'Enter respond.io conversation ID',
  },
  {
    value: 'whatsapp_direct', label: 'Whatsapp direct', specLabel: 'Why was Whatsapp direct used instead of respond.io?', specPlaceholder: 'e.g. Customer contacted personal number, respond.io was down...',
  },
  { value: 'email_inquiry', label: 'E-mail inquiry' },
  { value: 'direct_email', label: 'Direct email' },
  {
    value: 'other', label: 'Other - Please specify', specLabel: 'Please specify', specPlaceholder: 'e.g. Facebook ad, phone call, walk-in...',
  },
];

const TOKEN_ELIGIBLE_SOURCES = ['whatsapp_respondio', 'email_inquiry'];

function VisitorTrackingOriginStep({
  bookingId, onComplete, showCloseWarning, setShowCloseWarning,
}) {
  const [source, setSource] = useState('');
  const [specification, setSpecification] = useState('');
  const [hasToken, setHasToken] = useState(false);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isTokenEligible = TOKEN_ELIGIBLE_SOURCES.includes(source);
  const selectedOption = ORIGIN_OPTIONS.find((o) => o.value === source);
  const hasSpecField = !!selectedOption?.specLabel;

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const visitorTracking = {
        origin: {
          source,
          ...(hasSpecField && specification.trim() ? { specification } : {}),
        },
        ...(hasToken && token ? { token } : {}),
      };

      await BookingService.updateBookingFields(bookingId, { visitor_tracking: visitorTracking });
      onComplete();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save origin data');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (!showCloseWarning) {
      setShowCloseWarning(true);
      return;
    }
    onComplete();
  };

  const isValid = source
    && (!hasSpecField || specification.trim())
    && (!hasToken || token.trim());

  return (
    <div className="p-4">
      <Alert variant="warning">
        <Alert.Heading className="h6">Visitor Tracking - No Automatic Match Found</Alert.Heading>
        <p className="mb-0">
          The booking could not be automatically matched to a visitor tracking session.
          Please specify where this request originated from.
        </p>
      </Alert>

      <Form>
        <Form.Group className="mb-3">
          <Form.Label>Where did the request originate from?</Form.Label>
          {ORIGIN_OPTIONS.map((option) => (
            <Form.Check
              key={option.value}
              type="radio"
              id={`origin-${option.value}`}
              name="origin_source"
              label={option.label}
              value={option.value}
              checked={source === option.value}
              onChange={(e) => {
                setSource(e.target.value);
                setSpecification('');
                setHasToken(false);
                setToken('');
              }}
            />
          ))}
        </Form.Group>

        {hasSpecField && (
          <Form.Group className="mb-3">
            <Form.Label>{selectedOption.specLabel}</Form.Label>
            <Form.Control
              type="text"
              placeholder={selectedOption.specPlaceholder}
              value={specification}
              onChange={(e) => setSpecification(e.target.value)}
            />
          </Form.Group>
        )}

        {isTokenEligible && (
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="has-token"
              label="Visitor tracking info was provided"
              checked={hasToken}
              onChange={(e) => {
                setHasToken(e.target.checked);
                if (!e.target.checked) setToken('');
              }}
            />
          </Form.Group>
        )}

        {isTokenEligible && hasToken && (
          <Form.Group className="mb-3">
            <Form.Label>Visitor Tracking Token</Form.Label>
            <Form.Control
              type="text"
              placeholder="Paste the visitor tracking token here"
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
          </Form.Group>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {showCloseWarning && (
          <Alert variant="danger" className="mb-3">
            Admin users will be notified that this step was skipped.
          </Alert>
        )}

        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid || loading}
          >
            {loading ? <Spinner size="sm" animation="border" /> : 'Save Origin'}
          </Button>
          <Button
            variant={showCloseWarning ? 'outline-danger' : 'outline-secondary'}
            onClick={handleSkip}
            disabled={loading}
          >
            {showCloseWarning ? 'Confirm Skip' : 'Skip'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default VisitorTrackingOriginStep;
